<svelte:options customElement={{
	tag: "bt-practice-sidebar",
}} />

<script lang="ts">
	import { onMount } from 'svelte';
	import * as Turbo from "@hotwired/turbo";
	import { eventBus } from '$shared/lib/eventBus';

	import Icon from '$shared/icons/Icon.svelte';
	import ActionButton from '$shared/action-button/ActionButton.svelte';
	import AILogo from '$shared/ai-logo/AILogo.svelte';

	export let visible = false;
	export let panelheader: string = 'AI Practice';
	export let betatext: string = 'Beta';
	export let disclaimerprefix: string = 'AI content may be inaccurate. Each conversation starts new. Copy this chat to your clipboard';
	export let disclaimerlinktext: string = 'here';

	let chatLog: Element;
	let copyButton: Element;
	let wasJustCopied = false;

	const closeSidebar = () => {
		// Handle comms with lesson sidebar
		visible = false;
		document.querySelector('bt-practice-sidebar')?.classList.add('hidden');
	}

	const writeToClipboard = async(text: string) => {
		try {
			navigator.clipboard.writeText(text);
			return true;
		} catch(error: any) {
			console.error(error.message);
			return false;
		}
	}

	onMount(() => {
		const sidebar: Element | null = document.querySelector('bt-practice-sidebar');

		// Need to define the pagination footers separately for different layout types)
		let contentGridContentItemVariant = document.querySelector('.content-group-grid--content-item');
		let innerPaginationFooter;
		let outerPaginationFooter;
		if (contentGridContentItemVariant) {
			innerPaginationFooter = document.querySelector('.content-group-grid--content-item bt-pagination-footer');
		} else {
			innerPaginationFooter= document.querySelector('.content-group-grid bt-pagination-footer');
		}
		if (contentGridContentItemVariant) {
			outerPaginationFooter = document.querySelector('.content-group-grid--content-item ~ bt-pagination-footer');
		} else {
			outerPaginationFooter = document.querySelector('.content-group-grid ~ bt-pagination-footer');
		}

		// Show and hide the sidebar at appropriate times
		if (sidebar) {
			sidebar.classList.add('hidden');
			eventBus.on('practice-open', (event) => {
				visible = true;
				sidebar?.classList.remove('hidden');
				if (innerPaginationFooter && outerPaginationFooter) {
					// Swap pagination footer visibility
					innerPaginationFooter.classList.remove('hidden');
					outerPaginationFooter.classList.add('hidden');
				}
				// Scroll to top if clicking the small screen version so the AI Practice header is visible
				if (window.scrollY >= 800) {
					window.scrollTo({top: 0, behavior: "instant"});
				}
				// Scroll chatlog element to lowest it can go
				setTimeout(() => {
					// Need the timeout to defer scrolling til rendered in DOM
					chatLog?.scrollTo(0, chatLog.scrollHeight);
				});

			})
			eventBus.on('practice-close', (event) => {
				visible = false;
				sidebar?.classList.add('hidden');
				if (innerPaginationFooter && outerPaginationFooter) {
					// Swap pagination footer visibility
					innerPaginationFooter?.classList.add('hidden');
					outerPaginationFooter?.classList.remove('hidden');
				}
			});

			// Wire up turbo form submit manually
			sidebar.shadowRoot?.addEventListener("submit", (event) => {
				Turbo.navigator.submitForm(event.target as HTMLFormElement);
			});

			// Submit form on enter key
			sidebar.addEventListener('keydown', (e: KeyboardEvent) => {
				if (e.code === 'Enter' && e.target instanceof HTMLTextAreaElement) {
					e.preventDefault();
					e.target?.parentNode?.querySelector('input[type="submit"]')?.click();
				}
			})
		}

		// Scroll to bottom of chat when a new message appears
		const chatMutationCallback = (mutationList: any, observer: MutationObserver) => {
			let allResponses = mutationList[0].target.querySelectorAll('.practice-sidebar__ai-response');
			let latestResponse = allResponses[allResponses.length-1];
			if (visible) {
				sidebar?.shadowRoot?.querySelector('.sidebar__scrollable')?.scrollTo({
					top: (latestResponse.offsetHeight + latestResponse.offsetTop),
					behavior: "smooth"
				})
			}
			observer.disconnect();
		};
		const chatObserver = new MutationObserver(chatMutationCallback);
		document.addEventListener('turbo:before-stream-render', async(event: any) => {
			if (event.srcElement.target == "practice_and_apply_form") {
				let textarea = sidebar?.querySelector('textarea');
				if (textarea) {
					textarea.value = '';
				}
				let conversationList = sidebar?.querySelector('.practice-sidebar__conversation');
				chatObserver.observe(conversationList, {childList: true});
			}
		});

		// Clipboard button logic
		copyButton.addEventListener('click', (event) => {
			const conversationList = sidebar?.querySelector('.practice-sidebar__conversation');
			if (conversationList) {
				let parsedForClipboard: any = Array.from(conversationList.children).map(element => {
					if (element.classList.contains('practice-sidebar__ai-response')) {
						let paragraphs = element.getElementsByTagName('p');
						let content = Array.from(paragraphs).map(element => {
							return element.innerText.trim();
						})
						content = content.join('\r\n');
						return `AI Practice: ${content}`
					} else if (element.classList.contains('practice-sidebar__user-response')) {
						return `You: ${element.innerText.trim()}`
					}
				})
				parsedForClipboard = parsedForClipboard.join('\r\n\r\n')
				writeToClipboard(parsedForClipboard).then((wasClipped) => {
					wasJustCopied = true;
					window.setTimeout(() => {
						wasJustCopied = false;
					}, 1000)
				})
			}
		});
	});
</script>

<section class={`sidebar ${visible ? '' : 'hidden'}`}>
	<div class="sidebar__header">
		<div class="sidebar__header__lockup">
			<AILogo></AILogo>
      <span class="sidebar__header__lockup__practice">{panelheader}</span>
	   	<div class="beta-badge"><span>{betatext}</span></div>
		</div>
	  <div class="sidebar__header__close">
			<ActionButton design="text-button" on:click={() => {closeSidebar()}}>
				<Icon icon="XAlt" size="medium"></Icon>
			</ActionButton>
		</div>
	</div>
	<div class="sidebar__scrollable" bind:this={chatLog}>
		<div class="sidebar__scrollable__log">
			<slot name="chatlog"></slot>
		</div>
	</div>
	<div class="sidebar__footer">
		<slot name="chatform"></slot>
		<div class="sidebar__footer__caveat">
			{disclaimerprefix} <button type="button" class="copy-button" bind:this={copyButton}>{disclaimerlinktext}</button>.<div class="copy-button-icon"><Icon size="small" icon="Check" hidden={!wasJustCopied} /></div>
		</div>
	</div>
</section>

<style>
	/* Settings and base styles for container */
	:global(bt-practice-sidebar) {
		grid-column-start: 4;
		grid-row-start: 1;
		background: white;
	}
	.sidebar, :global(bt-practice-sidebar) {
		--header-height: 108px;
		--footer-height: 72px;
		--sidebar-scrollable-gutter: 240px;
		--practice-footer-height: 10rem;
		--search-bar-height: 4.75rem;
		position: relative;
		z-index: 30;
	}
	:global(bt-practice-sidebar:has(.sidebar.hidden)) {
		display: none;
	}
	.sidebar {
		z-index: 1;
		padding: theme('spacing.6');
		padding-top: theme('spacing.4');
		min-width: 400px;
		overflow: hidden;
		height: 100%;
		position: relative;
		top: -4.75rem;
		border-left: 1px solid theme('colors.neutral.300');
		background: white;
		&:after {
			content: '';
			display: block;
			position: absolute;
			right: 0;
			top: 1.5rem;
			height: 100%;
			width: 5px;
			background: theme('colors.neutral.200');
		}
		@media (max-width: 30rem) {
			padding-left: 2rem;
			min-width: unset;
		}
	}
	/* Sidebar header styles */
	.sidebar__header {
		display: flex;
		flex-wrap: nowrap;
		width: 100%;
		justify-content: space-between;
		align-items: center;
		margin-bottom: theme('spacing.6');
		padding-bottom:  14px;
		border-bottom: 1px solid theme('colors.neutral.300');
		button {
			background: white;
		}
		a {
			text-decoration: none;
		}
	}

	.sidebar__header__lockup {
		display: flex;
		flex-wrap: nowrap;
		align-items: center;
		flex-grow: 1;
		.ai-loader {
			margin-right: theme('spacing.4');
		}
	}

	.sidebar__header__lockup__practice {
		font-family: theme('fontFamily.serif');
		font-size: theme('fontSize.xl');
		letter-spacing: -0.5px;
		margin-left: theme('spacing.2');
	}

	/* Styles for the scrollable portion + the cards therein */
	.sidebar__scrollable {
		/* dynamically set - -22rem based on actual space available */
		height: calc(100vh - 24rem);
		width: calc(100%);
		overflow-y: scroll;
		overscroll-behavior: contain;
		scrollbar-width: none;
		position: relative;
	}
	:global(
		.sidebar__scrollable > a.sidebar__card:last-child,
		.sidebar__scrollable > section.sidebar__section:last-child
	) {
		margin-bottom: var(--sidebar-scrollable-gutter);
	}
	.sidebar__scrollable__log {
		min-height: 100%;
		display: flex;
	}
	.sidebar__card {
		max-width: 100%;
		padding: .5rem;
		margin-bottom: .5rem;
		background: white;
		border-radius: 8px;
		border: 1px solid transparent;
		display: grid;
		grid-template-columns: 1.5rem auto;
		gap: .5rem;
		&:first-child {
			margin-top: 1rem;
		}
	}
	.sidebar__card--path {
		padding: 0;
		.sidebar__card__contents {
			grid-column: 1 / span 2;
		}
	}
	.sidebar__card__icon {
		grid-column: 1 / span 1;
		align-self: center;
		display: grid;
		place-content: center;
		width: 24px;
		height: 24px;
		border-radius: 50%;
		border: 1px solid black;
		position: relative;
	}
	.sidebar__card__contents {
		grid-column: 2 / span 1;
	}
	.sidebar__card__eyebrow {
		color: theme('colors.neutral.500');
		font-size: 12px;
		letter-spacing: 1px;
		line-height: 16px;
		text-transform: uppercase;
		display: block;
	}
	.sidebar__card h3 {
		color: black;
		font-size: 16px;
		line-height: 1.5;
		margin: 0;
		display: block;
	}
	.sidebar__card__subhead {
		color: theme('colors.neutral.500');
		font-size: 12px;
		line-height: 16px;
		font-weight: 400;
		display: block;
	}

	.sidebar__section:not(:last-child) {
		.sidebar__card:last-child {
			margin-bottom: .5rem;
		}
	}

	.sidebar__section--collapsed .sidebar__section__contents {
		display: none;
	}

	.sidebar__section__toggle__text {
		line-height: 24px;
		font-weight: 500;
		font-size: 1rem;
	}
	:global(.sidebar__section .menu-button) {
		position: sticky;
		top: -1rem;
		z-index: 1;
	}
	:global(.sidebar__section .action-button.sidebar-section__collapse-button) {
		position: initial;
		top: initial;
		z-index: 1;
		padding-right: .5rem;
		padding-left: .5rem;
	}
	:global(.sidebar__section .action-button.sidebar-section__collapse-button > .sidebar__card) {
		padding: 0;
		margin-top: 0;
		&:first-child {
			margin-top: 0;
		}
	}

	.sidebar__footer {
		width: 100%;
		height: var(--practice-footer-height);
		margin-top: 1rem;
	}

	.sidebar__footer__caveat {
		font-size: theme('fontSize.sm');
		color: theme('colors.neutral.500');
		padding-right: 4rem;
	}

	.beta-badge {
		align-self: flex-start;
		display: inline-block;
		margin-left: .5rem;
		span {
			background: theme('colors.sky-blue-light');
			color: theme('colors.sky-blue');
			display: inline-block;
			width: min-content;
			padding: 2px 6px 4px 6px;
			border-radius: 16px;
			font-size: theme('fontSize.xs');
			line-height: 1rem;
			font-weight: 500;
			position: relative;
			top: 0;
		}
	}

	.copy-button {
		appearance: none;
		padding: unset;
		margin: unset;
		background: unset;
		border: unset;
		outline: unset;
		cursor: pointer;
		font-family: theme('fontFamily.sans');
		color: theme('colors.neutral.500');
		font-size: theme('fontSize.sm');
		text-decoration: underline;
		&:hover {
			color: theme('colors.neutral.700');
		}
		&:focus-visible {
			outline: 1px solid theme('colors.sky-blue');
		}
	}

	.copy-button-icon {
		position: relative;
		display: inline-block;
		top: theme('spacing.1');
		margin-left: theme('spacing.1');
		color: theme('colors.green.400');
	}
</style>
